import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateSchema, isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
import { taskSummarySchema } from '../tasks/tasks.js';
import { attachmentSchema, categorySchema, memberRefSchema } from './topics.js';
import { votingOutcomeSchema } from './voting.js';
export const DECISION_TYPES = [
    'SIMPLE_APPROVAL',
    'MULTIPLE_CHOICE',
    'CONSENSUS',
    'OWNER_DECISION',
    'INFORMATIONAL',
    'EMERGENCY',
];
export const decisionTypeSchema = z.enum(DECISION_TYPES);
/** Types recorded without a vote. Vote-based decisions are drafted from a finalized round. */
export const DIRECT_DECISION_TYPES = ['OWNER_DECISION', 'INFORMATIONAL'];
export const directDecisionTypeSchema = z.enum(DIRECT_DECISION_TYPES);
export const DECISION_STATUSES = ['DRAFT', 'FINALIZED', 'DISCARDED'];
export const decisionStatusSchema = z.enum(DECISION_STATUSES);
export const SUPERSESSION_KINDS = ['AMENDS', 'REVERSES', 'REPLACES'];
export const supersessionKindSchema = z.enum(SUPERSESSION_KINDS);
export const decisionRefSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    status: decisionStatusSchema,
});
export const decisionSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    decisionType: decisionTypeSchema,
    status: decisionStatusSchema,
    category: categorySchema.nullable(),
    sourceTopic: z.object({ id: uuidSchema, number: z.number().int(), title: z.string() }).nullable(),
    /** Outcome of the voting round behind a vote-based decision. */
    roundOutcome: votingOutcomeSchema.nullable(),
    decidedAt: isoDateTimeSchema.nullable(),
    effectiveDate: isoDateSchema.nullable(),
    finalizedAt: isoDateTimeSchema.nullable(),
    supersedes: decisionRefSchema.nullable(),
    /** Finalized decision that supersedes this one, if any. */
    supersededBy: decisionRefSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const decisionPageSchema = cursorPageSchema(decisionSummarySchema);
export const decisionListQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    status: decisionStatusSchema.optional(),
    decisionType: decisionTypeSchema.optional(),
    categoryId: uuidSchema.optional(),
    topicId: uuidSchema.optional(),
    q: z.string().trim().min(2).max(200).optional(),
});
export const decisionPermissionsSchema = z.object({
    canEdit: z.boolean(),
    canFinalize: z.boolean(),
    canDiscard: z.boolean(),
    canCreateTasks: z.boolean(),
    canSupersede: z.boolean(),
    canAttach: z.boolean(),
});
/** Immutable record of who could vote, how they voted and which rule produced the result. */
export const decisionVoteRecordSchema = z.object({
    roundId: uuidSchema,
    roundNumber: z.number().int(),
    outcome: votingOutcomeSchema,
    policyName: z.string(),
    policyVersionNumber: z.number().int(),
    ruleSummary: z.array(z.string()),
    explanation: z.array(z.string()),
    voters: z.array(z.object({
        memberId: uuidSchema,
        displayName: z.string(),
        weight: z.string(),
        choice: z.string().nullable(),
        optionTitle: z.string().nullable(),
    })),
    finalizedAt: isoDateTimeSchema,
});
export const decisionDetailSchema = decisionSummarySchema.extend({
    statement: z.string(),
    rationale: z.string().nullable(),
    selectedOption: z.object({ id: uuidSchema, title: z.string() }).nullable(),
    votingRoundId: uuidSchema.nullable(),
    decisionOwner: memberRefSchema.nullable(),
    finalizedBy: z.string().nullable(),
    supersessionKind: supersessionKindSchema.nullable(),
    supersessionReason: z.string().nullable(),
    /** Key-argument comments of the source topic (evidence links). */
    keyArguments: z.array(z.object({
        commentId: uuidSchema,
        author: z.string(),
        excerpt: z.string(),
        createdAt: isoDateTimeSchema,
    })),
    voteRecord: decisionVoteRecordSchema.nullable(),
    attachments: z.array(attachmentSchema),
    tasks: z.array(taskSummarySchema),
    createdBy: z.string(),
    permissions: decisionPermissionsSchema,
    version: versionSchema,
});
const decisionTextSchema = {
    title: z.string().trim().min(3).max(200),
    statement: z.string().trim().min(3).max(10000),
    rationale: z.string().trim().max(10000).nullable().optional(),
};
const supersessionFields = {
    supersedesDecisionId: uuidSchema.nullable().optional(),
    supersessionKind: supersessionKindSchema.nullable().optional(),
    supersessionReason: reasonSchema.nullable().optional(),
};
function supersessionIsComplete(value) {
    const target = value.supersedesDecisionId ?? null;
    const kind = value.supersessionKind ?? null;
    const reason = value.supersessionReason ?? null;
    return target === null ? kind === null : kind !== null && reason !== null;
}
export const createDecisionRequestSchema = z
    .object({
    ...decisionTextSchema,
    decisionType: directDecisionTypeSchema,
    sourceTopicId: uuidSchema.nullable().optional(),
    categoryId: uuidSchema.nullable().optional(),
    decisionOwnerMemberId: uuidSchema.nullable().optional(),
    effectiveDate: isoDateSchema.nullable().optional(),
    ...supersessionFields,
    /** AI decision draft this record was confirmed from (the draft becomes ACCEPTED). */
    aiOutputId: uuidSchema.nullable().optional(),
})
    .refine(supersessionIsComplete, {
    message: 'A superseding decision needs the kind of change and a reason',
    path: ['supersessionReason'],
})
    .refine((value) => value.decisionType !== 'OWNER_DECISION' || (value.decisionOwnerMemberId ?? null) !== null, { message: 'Name the decision owner', path: ['decisionOwnerMemberId'] });
export const updateDecisionRequestSchema = z
    .object({
    version: versionSchema,
    title: decisionTextSchema.title.optional(),
    statement: decisionTextSchema.statement.optional(),
    rationale: decisionTextSchema.rationale,
    decisionOwnerMemberId: uuidSchema.nullable().optional(),
    effectiveDate: isoDateSchema.nullable().optional(),
    ...supersessionFields,
})
    .refine((value) => value.supersedesDecisionId === undefined &&
    value.supersessionKind === undefined &&
    value.supersessionReason === undefined
    ? true
    : supersessionIsComplete(value), {
    message: 'A superseding decision needs the kind of change and a reason',
    path: ['supersessionReason'],
});
export const finalizeDecisionRequestSchema = z.object({ version: versionSchema });
export const discardDecisionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
//# sourceMappingURL=decisions.js.map